import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, RootState } from '../../../store';
import { useSelector } from 'react-redux';
import BaseForm, { BaseFormSectionConfig } from '../../shared/FormComponents/BaseForm';
import { fetchPracticeAreas, fetchDepartments } from '../../../store/actions/DropdownActions';
import { createTeam, fetchAllTeams } from '../../../store/actions/organization/teamActions';
import { FormSchema, FormField } from '../../../utils/validation/form-schema';
import { DropdownOption } from '../../shared/TailwindComponents/Dropdown';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, teamFormTourSteps } from '../../shared/FeatureTour';
import { readErrorMessage } from '../../../utils/errorMessage';

interface TeamFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const TeamForm: React.FC<TeamFormProps> = ({ onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const practiceAreas = useSelector((state: RootState) => state.dropdown.dropdowns.practiceAreas) || [];
  const departments = useSelector((state: RootState) => state.dropdown.dropdowns.departments) || [];
  const userInfo = useSelector((state: RootState) => state.user?.userInfo);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'team-form',
    steps: teamFormTourSteps,
    readyToShow: true,
    autoStartDelay: 600,
  });

  useEffect(() => {
    dispatch(fetchPracticeAreas());
    dispatch(fetchDepartments());
  }, [dispatch]);

  // Create validation schema
  const schema = new FormSchema('TeamForm', [
    new FormField('name', 'title', true, 'Team Name', {
      minLength: 2,
      maxLength: 255,
    }),
    new FormField('description', 'body', false, 'Description', {
      maxLength: 5000,
    }),
    new FormField('practice_area_ids', 'text', false, 'Practice Areas'),
    new FormField('department_id', 'text', false, 'Department'),
    new FormField('members', 'text', false, 'Members'),
  ]);

  const practiceAreaOptions: DropdownOption[] = practiceAreas.map(area => ({
    value: String(area.value || area.id || ''),
    label: String(area.label || ''),
  }));

  const departmentOptions: DropdownOption[] = departments.map(dept => ({
    value: String(dept.value || dept.id || ''),
    label: String(dept.label || ''),
  }));

  const sections: BaseFormSectionConfig[] = [
    {
      title: t('Team Details'),
      description: t('Name the team and place it in your organization'),
      gridCols: 2,
      fields: [
        {
          name: 'name',
          type: 'input',
          label: t('Team Name'),
          placeholder: t('e.g., Family Law Group'),
          inputType: 'text',
          required: true,
          gridSize: 'half',
        },
        {
          name: 'department_id',
          type: 'dropdown',
          label: t('Department'),
          options: departmentOptions,
          placeholder: t('Select department'),
          customProps: { fullWidth: true },
          gridSize: 'half',
        },
        {
          name: 'practice_area_ids',
          type: 'dropdown',
          label: t('Practice Areas'),
          options: practiceAreaOptions,
          placeholder: t('Select practice areas'),
          customProps: { fullWidth: true, multiple: true },
          gridSize: 'full',
        },
        {
          name: 'description',
          type: 'textarea',
          label: t('Description'),
          placeholder: t('Optional description'),
          gridSize: 'full',
          rows: 3,
        },
      ],
    },
    {
      title: t('Members'),
      description: t('Add team members and designate a lead'),
      fields: [
        {
          // The section above already says "Members" and explains the field —
          // the control draws no header of its own.
          name: 'members',
          type: 'collaboratorSelector',
          label: '',
          required: false,
          gridSize: 'full',
          customProps: {
            enableLeadSelection: true,
          },
        },
      ],
    },
  ];

  const handleSubmit = async (data: Record<string, any>) => {
    setIsSubmitting(true);
    try {
      // Map collaborators to backend members format
      const collaborators = data.members || [];
      const members = collaborators.map((collab: any) => ({
        user_id: collab.collaborator_uuid || String(collab.user_id || ''),
        role: collab.is_lead ? 'lead' : 'member',
      }));

      // If no collaborator is marked as lead, add current user as lead
      const hasLeadInCollabs = members.some((m: any) => m.role === 'lead');
      const currentUid = userInfo?.UserUID;
      if (currentUid) {
        const currentUserInList = members.find((m: any) => m.user_id === currentUid);
        if (!hasLeadInCollabs) {
          if (currentUserInList) {
            currentUserInList.role = 'lead';
          } else {
            members.unshift({ user_id: currentUid, role: 'lead' });
          }
        } else if (!currentUserInList) {
          members.push({ user_id: currentUid, role: 'member' });
        }
      }

      const createPayload: any = {
        name: data.name,
        description: data.description,
        members,
      };
      if (data.practice_area_ids && data.practice_area_ids.length > 0) {
        createPayload.practice_area_ids = Array.isArray(data.practice_area_ids)
          ? data.practice_area_ids
          : [data.practice_area_ids];
      }
      if (data.department_id) {
        createPayload.department_id = data.department_id;
      }

      await dispatch(createTeam(createPayload) as any);
      await dispatch(fetchAllTeams() as any);
      onSuccess?.();
    } catch (error: any) {
      const errorMsg = readErrorMessage(error, String(error));
      throw new Error(errorMsg || t('Failed to create team. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <div className="relative">
    <div data-tour-id="team-form" className="absolute top-0 left-0 right-0 h-[120px] pointer-events-none z-10" />
    <BaseForm
      title={t('Create Team')}
      description={t('Create a new team and assign members')}
      sections={sections}
      schema={schema}
      initialValues={{
        name: '',
        description: '',
        practice_area_ids: [],
        department_id: '',
        members: [],
      }}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      submitLabel={t('Create Team')}
      cancelLabel={t('Cancel')}
      isSubmitting={isSubmitting}
      maxWidth="max-w-2xl"
      loadingText={t('Creating team...')}
    />
    </div>
    <FeatureTour
      steps={teamFormTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
    </>
  );
};

export default TeamForm;
